import React, {useEffect, useState, useRef} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Tooltip} from '@optimaxdev/design-system';
import {Approve, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {PRODUCT_TYPES} from 'constants/product';
import {ApplicationStoreType} from 'desktop/store';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';

import {getOrderReviewItems} from '../../store/selectors/orderReviewItems/orderReviewItems';

import s from './paymentMoneyBackGuarantee.scss';

export type PropsType = {
    isShowExtendedText: boolean;
};

/**
 * Money Back Guarantee for checkout
 *
 * @constructor
 */
export const PaymentMoneyBackGuarantee: React.FC<PropsType> = ({isShowExtendedText}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const [isTooltipVisible, setTooltipVisibility] = useState<boolean>(false);
    const timeout = useRef<number>();
    const {isOneStepAvailable} = useIsOneStepAvailable();

    useEffect(() => {
        if (isTooltipVisible) {
            timeout.current = window.setTimeout(() => {
                sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
                    message: 'Money Back Guarantee - Hover',
                    method: 'Payment',
                });
            }, 3000);
        }
        return () => clearTimeout(timeout.current);
    }, [isTooltipVisible]);

    return (
        <div className={classNames(s.container, {[s.containerFacelift]: isFacelift})}>
            <div className={classNames(s.text, {[s.oneStep]: isOneStepAvailable})}>
                <span className={s.tooltipWrapper}>
                    <div className={classNames(s.iconWrap, {[s.iconWrapFacelift]: isFacelift})}>
                        <Icon
                            className={classNames(s.icon, {[s.iconFacelift]: isFacelift})}
                            icon={Approve}
                            size="small"
                            strokeWidth={2.5}
                        />
                    </div>
                    <Tooltip
                        content={
                            <div className={s.tooltip}>
                                <div className={s.header}>Money Back Guaranteed</div>
                                <p className={s.content}>
                                    We’re confident you’ll love your new glasses. If for any reason
                                    you’re not completely satisfied within the first 14 days, you’re
                                    entitled to a full refund or a 100% store credit, no questions
                                    asked.
                                </p>
                            </div>
                        }
                        closeTrigger="hover"
                        isOpen={isTooltipVisible}
                        onVisibilityChanged={setTooltipVisibility}
                        openTrigger="hover"
                        position="top"
                    >
                        <strong
                            className={classNames(s.guarantee, {[s.guaranteeFacelift]: isFacelift})}
                        >
                            100% Money Back Guarantee!&nbsp;
                        </strong>
                    </Tooltip>
                </span>
                {isShowExtendedText && (
                    <span className={classNames(s.extended, {[s.extendedFacelift]: isFacelift})}>
                        Not satisfied with your glasses? Return them for a refund.
                    </span>
                )}
            </div>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    isShowExtendedText: !getOrderReviewItems(store).some(
        item =>
            item.product.type !== PRODUCT_TYPES.SUNGLASSES &&
            item.product.type !== PRODUCT_TYPES.EYEGLASSES,
    ),
});

export const PaymentMoneyBackGuaranteeConnected =
    connect(mapStateToProps)(PaymentMoneyBackGuarantee);
